import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchBreakStatus, fetchDayActivityTimeline, fetchLeaveStatus } from '../../services/attendanceService';
import { subscribeAttendanceSessionChanged } from '../../services/attendanceSessionBus';
import { normalizeMessageContentHtml } from '../../utils/fileUtils';
import { formatEstimateTime } from '../../utils/estimateTime';
import { isDisplayableProductivityEntry, filterProductivityEntriesForCompletedList } from '../../utils/productivityEntryUtils';
import { formatPresenceLabel, presenceDotClass } from '../../utils/presenceLabelUtils';
import WorkSessionWatch from '../attendance/WorkSessionWatch';

const presenceUserFromSession = (user, session, leaveStatus) => {
  const leaveCleared = leaveStatus && leaveStatus.hasLeave === false;
  if (!leaveCleared && (user?.status === 'ON_LEAVE' || leaveStatus?.hasLeave)) {
    const leaveLabel = leaveStatus?.leaveTypeLabel
      ? `${leaveStatus.leaveTypeLabel} leave${leaveStatus.reason ? ` · ${leaveStatus.reason}` : ''}`
      : (user?.statusMessage || leaveStatus?.reason || 'On leave');
    return {
      ...user,
      status: 'ON_LEAVE',
      statusMessage: leaveLabel,
      statusSource: 'ATTENDANCE',
      onBreak: false,
    };
  }
  if (!user && !session) return null;
  if (session?.onBreak) {
    return {
      ...user,
      status: 'AWAY',
      statusMessage: session.reason || user?.statusMessage || null,
      statusSource: 'ATTENDANCE',
      onBreak: true,
    };
  }
  if (session?.loggedOut) {
    return {
      ...user,
      status: 'LOGGED_OUT',
      statusMessage: user?.statusMessage || null,
      onBreak: false,
    };
  }
  if (!session?.checkedIn && !user?.status) {
    return { ...user, status: 'OFFLINE', onBreak: false };
  }
  return {
    ...user,
    onBreak: false,
  };
};

/** Live presence under Productivity today. Updates on attendance/status changes only. */
export const CurrentPresenceStatus = ({ enabled = true }) => {
  const user = useSelector((state) => state.auth.user);
  const [session, setSession] = useState(null);
  const [leaveStatus, setLeaveStatus] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const applySession = (next) => {
      if (!cancelled && next) setSession(next);
    };
    const applyLeave = (next) => {
      if (!cancelled) setLeaveStatus(next || null);
    };

    fetchBreakStatus()
      .then(applySession)
      .catch(() => {});
    fetchLeaveStatus()
      .then(applyLeave)
      .catch(() => {});

    const unsubscribe = subscribeAttendanceSessionChanged((detail) => {
      if (detail?.status) {
        applySession(detail.status);
      } else {
        fetchBreakStatus({ force: true })
          .then(applySession)
          .catch(() => {});
      }
      fetchLeaveStatus()
        .then(applyLeave)
        .catch(() => {});
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [enabled]);

  const presenceUser = useMemo(
    () => presenceUserFromSession(user, session, leaveStatus),
    [user, session, leaveStatus],
  );

  if (!enabled || !presenceUser) return null;

  const label = formatPresenceLabel(presenceUser);

  return (
    <p className="mt-1 inline-flex items-center gap-1.5 max-w-full min-w-0 text-xs sm:text-sm text-gray-600 dark:text-gray-300">
      <span className="text-[10px] sm:text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
        Status
      </span>
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${presenceDotClass(presenceUser)}`} />
      <span className="truncate font-medium">{label}</span>
    </p>
  );
};

export const HomePendingCard = ({
  title,
  count,
  hint,
  completedCount,
  selected = false,
  compact = false,
  pendingLabel = 'Pending',
  onClick,
}) => {
  const selectedTone = selected
    ? 'bg-orange-500 border-orange-500 text-white shadow-md'
    : 'bg-white border-orange-400 text-gray-900 hover:shadow-sm';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`relative w-full min-w-0 text-left border-2 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${selectedTone} ${
        compact
          ? 'rounded-xl px-2 py-1.5'
          : 'rounded-xl lg:rounded-2xl px-2 py-1.5 sm:px-3 sm:py-2.5 lg:px-4 lg:py-4 lg:min-w-[9.5rem] lg:flex-none flex-1'
      } ${!compact && typeof completedCount === 'number' ? 'lg:pb-8' : ''}`}
    >
      <div className={`flex gap-1 leading-tight ${compact ? 'items-baseline justify-between' : 'items-baseline justify-between lg:items-start lg:gap-3'}`}>
        <div className="min-w-0">
          {!compact ? (
            <p className={`hidden lg:block text-[11px] font-bold uppercase tracking-wider ${
              selected ? 'text-white' : 'text-orange-500'
            }`}
            >
              {pendingLabel}
            </p>
          ) : null}
          <h3 className={`min-w-0 truncate font-bold ${
            compact ? 'text-[11px]' : 'text-[11px] sm:text-sm lg:mt-0.5 lg:text-base lg:leading-tight'
          } ${selected ? 'text-white' : 'text-gray-900'}`}
          >
            {title}
          </h3>
          {!compact && hint ? (
            <p className={`mt-1.5 hidden lg:block text-xs leading-snug ${
              selected ? 'text-white/90' : 'text-gray-500'
            }`}
            >
              {hint}
            </p>
          ) : null}
        </div>
        <span className={`font-black tabular-nums leading-none flex-shrink-0 ${
          compact ? 'text-sm' : 'text-sm sm:text-xl lg:text-3xl'
        } ${selected ? 'text-white' : 'text-gray-900'}`}
        >
          {count}
        </span>
      </div>
      <p className={`mt-0.5 truncate text-[10px] leading-tight ${compact ? '' : 'lg:hidden'} ${
        selected ? 'text-white/90' : 'text-gray-500'
      }`}
      >
        {pendingLabel}
        {typeof completedCount === 'number' ? ` · ${completedCount} done` : ''}
      </p>
      {!compact && typeof completedCount === 'number' ? (
        <p className="hidden lg:block absolute bottom-3 right-4 text-xs font-bold tabular-nums text-emerald-600 bg-white/95 rounded-md px-1.5 py-0.5 shadow-sm">
          Completed - {completedCount}
        </p>
      ) : null}
    </button>
  );
};

const ProductivityRing = ({ percent, size = 'lg' }) => {
  const value = Math.max(0, Math.min(100, Number(percent) || 0));
  const compact = size === 'sm';
  const radius = compact ? 36 : 54;
  const stroke = compact ? 8 : 10;
  const view = compact ? 88 : 128;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  return (
    <div className={`relative flex-shrink-0 ${compact ? 'h-[4.75rem] w-[4.75rem]' : 'h-[4.75rem] w-[4.75rem] sm:h-28 sm:w-28 lg:h-40 lg:w-40'}`}>
      <svg viewBox={`0 0 ${view} ${view}`} className="h-full w-full -rotate-90">
        <circle
          cx={view / 2}
          cy={view / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          className="text-orange-100 dark:text-gray-700"
          strokeWidth={stroke}
        />
        <circle
          cx={view / 2}
          cy={view / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          className="text-emerald-500"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center px-1">
        <span className={`font-black tabular-nums text-emerald-600 dark:text-emerald-400 ${compact ? 'text-lg leading-none' : 'text-lg leading-none sm:text-2xl lg:text-3xl'}`}>{value}%</span>
        <span className={`font-semibold uppercase tracking-wider text-emerald-600 ${compact ? 'text-[8px]' : 'text-[8px] sm:text-[10px]'}`}>Done</span>
      </div>
    </div>
  );
};

const ATTENDANCE_META = {
  CHECK_IN: {
    title: 'Logged in',
    dot: 'bg-emerald-500 ring-emerald-200 dark:ring-emerald-800',
    line: 'bg-emerald-400/70',
  },
  BREAK_START: {
    title: 'Took a break',
    dot: 'bg-amber-500 ring-amber-200 dark:ring-amber-800',
    line: 'bg-amber-300/80',
  },
  BREAK_END: {
    title: 'Joined back',
    dot: 'bg-sky-500 ring-sky-200 dark:ring-sky-800',
    line: 'bg-sky-300/80',
  },
  RELOGIN: {
    title: 'Relogged in',
    dot: 'bg-teal-500 ring-teal-200 dark:ring-teal-800',
    line: 'bg-teal-300/80',
  },
  CHECK_OUT: {
    title: 'Logged out',
    dot: 'bg-rose-500 ring-rose-200 dark:ring-rose-800',
    line: 'bg-rose-300/70',
  },
};

const WORK_META = {
  TASK_DONE: {
    title: 'Finished task',
    dot: 'bg-emerald-500 ring-emerald-200 dark:ring-emerald-800',
    line: 'bg-emerald-400/70',
  },
  REMINDER_DONE: {
    title: 'Finished reminder',
    dot: 'bg-emerald-500 ring-emerald-200 dark:ring-emerald-800',
    line: 'bg-emerald-400/70',
  },
  WORKFLOW_DONE: {
    title: 'Finished workflow',
    dot: 'bg-emerald-500 ring-emerald-200 dark:ring-emerald-800',
    line: 'bg-emerald-400/70',
  },
};

const localTodayStr = () => new Date().toLocaleDateString('en-CA');

const GENERIC_ACTIVITY_SUBTITLES = /^(session start|session end|short break|back from break|back to active work|counted toward break on relogin)$/i;

const htmlToPlainText = (value) => {
  if (!value) return '';
  const raw = String(value);
  if (!/[<>]/.test(raw)) return raw.trim();
  if (typeof document === 'undefined') {
    return raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const tmp = document.createElement('div');
  tmp.innerHTML = raw;
  return (tmp.textContent || tmp.innerText || '').trim();
};

const isMeaningfulComment = (value) => {
  const text = htmlToPlainText(value);
  return Boolean(text) && !GENERIC_ACTIVITY_SUBTITLES.test(text);
};

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatClock = (value, fallback = '') => {
  if (!value) return fallback;
  if (typeof value === 'string' && /\d/.test(value) && (/AM|PM/i.test(value) || /^\d{1,2}:\d{2}/.test(value.trim()))) {
    return value;
  }
  const date = parseDate(value);
  if (!date) return fallback || String(value);
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};

const taskLabel = (task) => {
  const title = task?.title && String(task.title).trim();
  if (title) return title;
  const desc = task?.description || '';
  if (desc.trim() && !desc.trim().startsWith('<')) {
    return desc.trim().slice(0, 80);
  }
  return 'Task';
};

const reminderLabel = (reminder) => (reminder?.title && reminder.title.trim()) || 'Reminder';

const workflowLabel = (workflow) => (
  workflow?.workflowTitle
  || workflow?.label
  || (workflow?.content && String(workflow.content).trim())
  || 'Workflow'
);

const itemDurationLabel = (minutes) => formatEstimateTime(minutes);

const DoneSummarySection = ({ tasks, reminders, workflows, productivityEntries = [] }) => {
  const progressRows = filterProductivityEntriesForCompletedList(
    (Array.isArray(productivityEntries) ? productivityEntries : []).filter(isDisplayableProductivityEntry),
    tasks,
  ).map((entry, index) => ({
    key: `prod-${entry.id || index}`,
    tag: 'Progress',
    name: entry.displayLabel
      || `${entry.taskTitle || 'Task'} (completed from ${entry.fromPercent || 0}% to ${entry.toPercent || 0}%)`,
    duration: itemDurationLabel(entry.creditedMinutes),
    at: parseDate(entry.createdAt)?.getTime() ?? 0,
  }));
  const taskRows = tasks.map((task, index) => ({
    key: `task-${task.id || index}`,
    tag: 'Task',
    name: taskLabel(task),
    duration: itemDurationLabel(task.completionTimeInMinutes || task.estimateTimeInMinutes),
    at: parseDate(task.completed || task.updatedAt)?.getTime() ?? 0,
  }));
  const rows = [
    ...progressRows,
    ...taskRows,
    ...reminders.map((reminder, index) => ({
      key: `reminder-${reminder.id || index}`,
      tag: 'Reminder',
      name: reminderLabel(reminder),
      duration: itemDurationLabel(reminder.estimateTimeInMinutes),
      at: parseDate(reminder.updatedAt)?.getTime() ?? 0,
    })),
    ...workflows.map((workflow, index) => ({
      key: `workflow-${workflow.messageId || index}`,
      tag: 'Workflow',
      name: workflowLabel(workflow),
      duration: itemDurationLabel(workflow.completionTimeInMinutes),
      at: parseDate(workflow.completedAt || workflow.updatedAt)?.getTime() ?? 0,
    })),
  ].sort((a, b) => a.at - b.at);

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm p-3 sm:p-4">
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Done today</h4>
        <p className="text-xs font-semibold tabular-nums text-emerald-600">
          {rows.length} completed
        </p>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
          Nothing completed yet today.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((row) => (
            <li key={row.key} className="flex items-center gap-2 min-w-0">
              <span className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200/80 dark:border-emerald-800 rounded-md px-1.5 py-0.5">
                {row.tag}
              </span>
              <span className="min-w-0 text-sm font-medium text-gray-800 dark:text-gray-100 truncate" title={row.name}>
                {row.name}
              </span>
              {row.duration ? (
                <span className="ml-auto flex-shrink-0 text-[11px] font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
                  {row.duration}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const DayCommentsSection = ({ comments }) => {
  if (!comments.length) return null;
  return (
    <div className="rounded-2xl border border-rose-200/80 dark:border-rose-900/40 bg-white dark:bg-gray-800 shadow-sm p-4">
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Comments</h4>
        <p className="text-xs font-semibold tabular-nums text-rose-600 dark:text-rose-400">
          {comments.length} {comments.length === 1 ? 'note' : 'notes'}
        </p>
      </div>
      <div className="space-y-3">
        {comments.map((comment) => (
          <div key={comment.id} className="rounded-xl border border-gray-100 dark:border-gray-700/70 bg-gray-50/90 dark:bg-gray-900/50 px-3 py-2.5">
            {comment.timeLabel ? (
              <p className="text-[11px] font-semibold tabular-nums text-gray-500 dark:text-gray-400 mb-1.5">
                {comment.timeLabel}
              </p>
            ) : null}
            {comment.isHtml ? (
              <div
                className="prose prose-sm dark:prose-invert max-w-none text-sm text-gray-700 dark:text-gray-300 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: comment.html }}
              />
            ) : (
              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                {comment.text}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const DaySummaryBlocks = ({ summary }) => {
  if (!summary?.startTime) return null;
  const loggedOut = Boolean(summary.endTime) && !summary.activeDay;
  const blocks = [
    { label: 'Start time', value: summary.startTime },
    { label: 'Breaks', value: summary.breakTime || '0m' },
  ];
  if (loggedOut) {
    blocks.push({ label: 'End time', value: summary.endTime });
    blocks.push({ label: 'Total time', value: summary.totalTime || '0m' });
  }
  return (
    <div className={`mb-4 grid gap-2 ${loggedOut ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2'}`}>
      {blocks.map((block) => (
        <div
          key={block.label}
          className="min-w-0 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-2.5 py-2 text-center"
        >
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 truncate">
            {block.label}
          </p>
          <p className="mt-0.5 text-sm font-bold tabular-nums text-gray-900 dark:text-white truncate" title={block.value}>
            {block.value}
          </p>
        </div>
      ))}
    </div>
  );
};

const DayTimeline = ({
  entries,
  loading,
  summary = null,
  heading = 'Timeline',
  emptyMessage = 'Your day will show up here after you log in, take a break, or finish work.',
}) => (
  <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm p-3 sm:p-4">
    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{heading}</h4>
    <DaySummaryBlocks summary={summary} />
    {loading ? (
      <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">Loading timeline…</p>
    ) : entries.length === 0 ? (
      <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
        {emptyMessage}
      </p>
    ) : (
      <ol>
        {entries.map((entry, index) => {
          const meta = ATTENDANCE_META[entry.type] || WORK_META[entry.type] || {
            title: entry.title || entry.type,
            dot: 'bg-gray-400 ring-gray-200 dark:ring-gray-700',
            line: 'bg-gray-300/80 dark:bg-gray-600/80',
          };
          const isLast = index === entries.length - 1;
          const isDone = Boolean(WORK_META[entry.type]);
          return (
            <li key={entry.id || `${entry.type}-${index}`} className="relative flex gap-3 pb-5 last:pb-0">
              <div className="relative flex flex-col items-center w-5 flex-shrink-0">
                <span className={`relative z-[1] mt-1 w-3.5 h-3.5 rounded-full ring-4 ${meta.dot}`} aria-hidden />
                {!isLast && (
                  <span className={`absolute top-4 bottom-[-1.25rem] w-0.5 rounded-full ${meta.line}`} aria-hidden />
                )}
              </div>
              <div className="min-w-0 flex-1 -mt-0.5">
                <div className="flex items-baseline justify-between gap-2">
                  <p className={`text-sm font-semibold ${
                    isDone ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-900 dark:text-gray-100'
                  }`}
                  >
                    {entry.title || meta.title}
                  </p>
                  <p className="text-xs font-semibold tabular-nums text-gray-500 dark:text-gray-400 flex-shrink-0">
                    {entry.timeLabel || '—'}
                  </p>
                </div>
                {entry.subtitle ? (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 truncate" title={entry.subtitle}>
                    {entry.subtitle}
                  </p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    )}
  </div>
);

const useDayActivity = ({
  date,
  ownerUserId,
  enabled = true,
  liveUpdates = false,
  completedTasks = [],
  completedReminders = [],
  completedWorkflows = [],
}) => {
  const [attendanceEntries, setAttendanceEntries] = useState([]);
  const [daySummary, setDaySummary] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(Boolean(enabled));

  useEffect(() => {
    if (!enabled || !date) {
      setTimelineLoading(false);
      setAttendanceEntries([]);
      setDaySummary(null);
      return undefined;
    }

    let cancelled = false;

    const loadTimeline = async () => {
      setTimelineLoading(true);
      try {
        const data = await fetchDayActivityTimeline(date, ownerUserId);
        if (cancelled) return;
        const entries = Array.isArray(data?.entries) ? data.entries : [];
        setAttendanceEntries(entries);
        const checkIn = entries.find((entry) => entry.type === 'CHECK_IN');
        const checkOut = [...entries].reverse().find((entry) => entry.type === 'CHECK_OUT');
        const startTime = formatClock(data?.checkInTime || checkIn?.timeLabel || checkIn?.eventAt || '');
        const endTime = formatClock(data?.checkOutTime || '');
        setDaySummary(startTime ? {
          startTime,
          breakTime: data?.totalBreakTime || (data?.totalBreakMinutes ? `${data.totalBreakMinutes}m` : '0m'),
          endTime: data?.activeDay ? '' : (endTime || formatClock(checkOut?.timeLabel || checkOut?.eventAt || '')),
          totalTime: data?.totalTime || data?.totalTimeSpent || '',
          activeDay: data?.activeDay != null ? Boolean(data.activeDay) : !endTime,
        } : null);
      } catch {
        if (!cancelled) {
          setAttendanceEntries([]);
          setDaySummary(null);
        }
      } finally {
        if (!cancelled) setTimelineLoading(false);
      }
    };

    loadTimeline();
    const unsubscribe = liveUpdates
      ? subscribeAttendanceSessionChanged(() => {
        loadTimeline();
      })
      : () => {};
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [date, ownerUserId, enabled, liveUpdates]);

  const comments = useMemo(() => (
    (attendanceEntries || [])
      .filter((entry) => entry.type === 'CHECK_OUT' && isMeaningfulComment(entry.subtitle))
      .map((entry, index) => {
        const raw = entry.subtitle || '';
        const isHtml = /<\/?[a-z][\s\S]*>/i.test(raw);
        return {
          id: entry.eventId || `comment-${index}`,
          timeLabel: formatClock(entry.timeLabel || entry.eventAt),
          html: isHtml ? normalizeMessageContentHtml(raw) : '',
          text: htmlToPlainText(raw),
          isHtml,
        };
      })
  ), [attendanceEntries]);

  const timelineEntries = useMemo(() => {
    const items = [];

    attendanceEntries.forEach((entry, index) => {
      const type = entry.type || '';
      const meta = ATTENDANCE_META[type];
      const at = parseDate(entry.eventAt);
      const rawSubtitle = entry.subtitle && !/^(CHECK_|BREAK_|RELOGIN)/.test(entry.subtitle)
        ? entry.subtitle
        : (entry.subtitle || '');
      items.push({
        id: entry.eventId || `attendance-${type}-${index}`,
        type,
        title: entry.title && !/^(CHECK_|BREAK_|RELOGIN)/.test(entry.title) ? entry.title : (meta?.title || entry.title || type),
        subtitle: type === 'CHECK_OUT' && isMeaningfulComment(rawSubtitle)
          ? 'Comment added'
          : htmlToPlainText(rawSubtitle),
        timeLabel: formatClock(entry.timeLabel || entry.eventAt),
        at: at ? at.getTime() : index,
      });
    });

    completedTasks.forEach((task, index) => {
      const at = parseDate(task.completed || task.updatedAt);
      items.push({
        id: `task-${task.id || index}`,
        type: 'TASK_DONE',
        title: 'Finished task',
        subtitle: taskLabel(task),
        timeLabel: formatClock(task.completed || task.updatedAt),
        at: at ? at.getTime() : Number.MAX_SAFE_INTEGER - 30 + index,
      });
    });

    completedReminders.forEach((reminder, index) => {
      const at = parseDate(reminder.updatedAt);
      items.push({
        id: `reminder-${reminder.id || index}`,
        type: 'REMINDER_DONE',
        title: 'Finished reminder',
        subtitle: reminderLabel(reminder),
        timeLabel: formatClock(reminder.updatedAt),
        at: at ? at.getTime() : Number.MAX_SAFE_INTEGER - 20 + index,
      });
    });

    completedWorkflows.forEach((workflow, index) => {
      const at = parseDate(workflow.completedAt || workflow.updatedAt);
      const duration = formatEstimateTime(workflow.completionTimeInMinutes);
      items.push({
        id: `workflow-${workflow.messageId || index}`,
        type: 'WORKFLOW_DONE',
        title: 'Finished workflow',
        subtitle: duration ? `${workflowLabel(workflow)} · ${duration}` : workflowLabel(workflow),
        timeLabel: formatClock(workflow.completedAt || workflow.updatedAt),
        at: at ? at.getTime() : Number.MAX_SAFE_INTEGER - 10 + index,
      });
    });

    return items.sort((a, b) => a.at - b.at);
  }, [attendanceEntries, completedTasks, completedReminders, completedWorkflows]);

  return { comments, timelineEntries, timelineLoading, daySummary };
};

export const DayActivityLog = ({
  date,
  ownerUserId,
  completedTasks = [],
  completedReminders = [],
  completedWorkflows = [],
  timelineHeading = 'Timeline',
  emptyTimelineMessage = 'No activity recorded for this day.',
}) => {
  const { comments, timelineEntries, timelineLoading, daySummary } = useDayActivity({
    date,
    ownerUserId,
    enabled: Boolean(date),
    liveUpdates: false,
    completedTasks,
    completedReminders,
    completedWorkflows,
  });

  return (
    <div className="space-y-3 mt-3 animate-fadeIn">
      <DayCommentsSection comments={comments} />
      <DayTimeline
        entries={timelineEntries}
        loading={timelineLoading}
        summary={daySummary}
        heading={timelineHeading}
        emptyMessage={emptyTimelineMessage}
      />
    </div>
  );
};

const TodayProductivityChart = ({
  productivityPct = 0,
  plannedMins = 0,
  doneMins = 0,
  denominatorLabel = 'office minutes',
  tasksPicked = 0,
  tasksDone = 0,
  remindersPlanned = 0,
  remindersDone = 0,
  workflowsPending = 0,
  workflowsDone = 0,
  completedTasks = [],
  completedReminders = [],
  completedWorkflows = [],
  productivityEntries = [],
  ownerUserId,
  showActivityLog = true,
  heading = 'Productivity today',
  date,
  showWorkWatch = false,
  headerActions = null,
  compact = false,
}) => {
  const { timelineEntries, timelineLoading, daySummary } = useDayActivity({
    date: date || localTodayStr(),
    ownerUserId,
    enabled: showActivityLog,
    liveUpdates: showActivityLog,
    completedTasks,
    completedReminders,
    completedWorkflows,
  });

  return (
    <div className={`space-y-3 sm:space-y-4 animate-fadeIn`}>
      <div className={`rounded-2xl border border-orange-200/80 dark:border-orange-900/40 bg-white dark:bg-gray-800 shadow-sm flex items-center min-w-0 overflow-hidden ${
        compact ? 'p-2.5 gap-2.5' : 'p-2.5 sm:p-4 lg:p-5 gap-2.5 sm:gap-4 lg:gap-6'
      }`}
      >
        <ProductivityRing percent={productivityPct} size={compact ? 'sm' : 'lg'} />
        <div className="flex-1 min-w-0 w-full overflow-hidden">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 min-w-0">
            <h3 className={`min-w-0 flex-1 font-bold text-gray-900 dark:text-white leading-tight ${compact ? 'text-sm' : 'text-base sm:text-lg'}`}>{heading}</h3>
            {headerActions ? (
              <div className="flex-shrink-0">{headerActions}</div>
            ) : null}
            {showWorkWatch ? (
              <div className="w-full flex justify-end min-w-0">
                <WorkSessionWatch enabled={showWorkWatch} />
              </div>
            ) : null}
          </div>
          <CurrentPresenceStatus enabled={showWorkWatch} />
          <p className={`text-gray-500 dark:text-gray-400 mt-1 ${compact ? 'text-[11px]' : 'text-sm'}`}>
            <span className="font-semibold text-emerald-600 tabular-nums">{doneMins}</span>
            {' '}of {plannedMins} {denominatorLabel}
          </p>
          <div className={`mt-2 sm:mt-3 grid grid-cols-3 ${compact ? 'gap-1.5' : 'gap-2'}`}>
            <div className={`rounded-xl bg-orange-50 dark:bg-orange-950/40 border border-orange-100 dark:border-orange-900/50 ${compact ? 'px-1.5 py-1.5' : 'px-3 py-2'}`}>
              <p className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-wide text-orange-600 dark:text-orange-300 truncate">Tasks</p>
              <p className={`font-bold text-gray-900 dark:text-white tabular-nums ${compact ? 'text-xs' : 'text-sm'}`}>
                <span className="text-emerald-600">{tasksDone}</span>
                <span className="text-gray-400 font-medium">/{tasksPicked}</span>
              </p>
            </div>
            <div className={`rounded-xl bg-white dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 ${compact ? 'px-1.5 py-1.5' : 'px-3 py-2'}`}>
              <p className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-wide text-gray-500 truncate">Reminders</p>
              <p className={`font-bold text-gray-900 dark:text-white tabular-nums ${compact ? 'text-xs' : 'text-sm'}`}>
                <span className="text-emerald-600">{remindersDone}</span>
                <span className="text-gray-400 font-medium">/{remindersPlanned}</span>
              </p>
            </div>
            <div className={`rounded-xl bg-orange-50 dark:bg-orange-950/40 border border-orange-100 dark:border-orange-900/50 ${compact ? 'px-1.5 py-1.5' : 'px-3 py-2'}`}>
              <p className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-wide text-orange-600 dark:text-orange-300 truncate">Workflows</p>
              <p className={`font-bold text-gray-900 dark:text-white tabular-nums ${compact ? 'text-xs' : 'text-sm'}`}>
                <span className="text-emerald-600">{workflowsDone}</span>
                <span className="text-gray-400 font-medium">/{workflowsPending + workflowsDone}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {showActivityLog && (
        <>
          <DoneSummarySection
            tasks={completedTasks}
            reminders={completedReminders}
            workflows={completedWorkflows}
            productivityEntries={productivityEntries}
          />

          <DayTimeline entries={timelineEntries} loading={timelineLoading} summary={daySummary} />
        </>
      )}
    </div>
  );
};

export default TodayProductivityChart;
